import { SET_CURRENT_COMPANY, SET_LOADER } from './type'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { AxiosError, AxiosResponse } from 'axios'
import {
  IAddBranchSettingDataAPI,
  IGetBranchDetailRes,
  IUpdateBranchSettingDataAPI,
} from 'interfaces/redux/branch-setting'
import { IParamCreateGoalVersion } from 'interfaces/redux/goal'
import {
  IDownloadExcelTemplateApi,
  IFectchScope,
  IFetchManualDataApi,
  IFetchURegistrationDataApi,
} from 'interfaces/redux/registration'
import { store } from 'redux/store/store'
import { ROLES } from 'utils/constants/enums'
import { LS_USER_ID, LS_USER_INFO } from 'utils/constants/localStorage'
import { apiHelper, authHelper, commonHelper } from 'utils/helpers'

const setLoader = (data: boolean = false) => ({
  type: SET_LOADER,
  data,
})

export const fetchGetDashboard = createAsyncThunk(
  'fetchGetDashboard',
  async ({ isLoading, onSuccess, onError }: IParamsAPI<IDashboardPage[]>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getDashboard()
      if (onSuccess) {
        onSuccess(res?.data?.data)
        return res?.data?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchLogin = createAsyncThunk(
  'fetchLogin',
  async ({ data, isLoading, onSuccess, onError }: IFetchLoginDataAPI) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.login(data)
      const result = res?.data?.data
      if (
        result?.user?.role === ROLES.USER_PARENT ||
        result?.user?.role === ROLES.USER_MANAGER ||
        result?.user?.role === ROLES.USER_VIEWER ||
        result?.user?.role === ROLES.USER_FACILITY_MANAGEMENT ||
        result?.user?.role === ROLES.USER_BROWSE_FACILITY ||
        result?.user?.role === ROLES.USER_REGISTRATION
      ) {
        if (result.token.accessToken) {
          authHelper.setAccessToken(
            result.token.accessToken ?? '',
            result?.token?.expiresIn,
          )
          commonHelper.getListScope()
          if (result.user.id) {
            localStorage.setItem(LS_USER_ID, result.user.id ?? '')
          }
        }
        if (onSuccess) {
          onSuccess(result)
          return result
        }
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

// ===========================================================================================================
export const fetchGetAnalysisAll = createAsyncThunk(
  'fetchGetAnalysisAll',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getAnalysisAll(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetNameRegistrationData = createAsyncThunk(
  'fetchGetNameRegistrationData',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
    ...props
  }: IParamsAPI<any>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      props.onStart?.()
      const res = await apiHelper.getNameRegistrationData(params)
      if (onSuccess) {
        onSuccess(res?.data?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      props.onFinally?.()
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetBranches = createAsyncThunk(
  'fetchGetBranches',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
    id,
    ...props
  }: IParamsAPI<any>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      props.onStart?.()
      const res = id
        ? await apiHelper.getBranchesOfCompanyInvited(params, id)
        : await apiHelper.getBranches(params)
      if (onSuccess) {
        onSuccess(res?.data?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      props.onFinally?.()
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchUpdateBranchSelect = createAsyncThunk(
  'fetchUpdateBranchSelect',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const { id } = JSON.parse(localStorage.getItem(LS_USER_INFO) ?? '')
      const res = await apiHelper.updateBranchSelect(id, params)
      if (onSuccess) {
        onSuccess(res?.data?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetRegisUsers = createAsyncThunk(
  'fetchGetRegisUsers',
  async ({ params, isLoading, onSuccess, onError }: IParamsAPI<any>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getRegistrationUsers(params)
      if (onSuccess) {
        onSuccess(res?.data?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchResendVerifyUserChild = createAsyncThunk(
  'fetchResendVerifyUserChild',
  async ({ id, params, isLoading, onSuccess, onError }: IParamsAPI<any>) => {
    store.dispatch(setLoader(isLoading))

    try {
      const res = await apiHelper.resendVerifyUserChild(id, params)
      if (onSuccess) {
        onSuccess(res?.data?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetDetailUser = createAsyncThunk(
  'fetchGetDetailUser',
  async ({ id, isLoading, onSuccess, onError }: IParamsAPI<IDetailUser>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getDetailUserInfo(id)
      if (onSuccess) {
        onSuccess(res?.data?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchAddRegisUserInfo = createAsyncThunk(
  'fetchAddRegisUserInfo',
  async ({ data, isLoading, onSuccess, onError }: IAddUserSettingDataAPI) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.addRegistrationUserInfo(data)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchEditRegisUserInfo = createAsyncThunk(
  'fetchEditRegisUserInfo',
  async ({
    id,
    data,
    isLoading,
    onSuccess,
    onError,
  }: IUpdateUserSettingDataAPI) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.editRegistrationUserInfo(id, data)
      if (onSuccess) {
        onSuccess(res?.data?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

// ===========================================================================================================

export const fetchForgotPassword = createAsyncThunk(
  'fetchForgotPassword',
  async ({ data, isLoading, onSuccess, onError }: IForgotPasswordDataAPI) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.forgotPassWord(data)
      if (onSuccess) {
        onSuccess(res?.data)
      }
      return res?.data
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchResetPassword = createAsyncThunk(
  'fetchResetPassword',
  async ({ data, isLoading, onSuccess, onError }: IResetPasswordDataAPI) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.resetPassword(data)
      if (onSuccess) {
        onSuccess(res?.data)
      }
      return res?.data
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetAnalysisScope1 = createAsyncThunk(
  'fetchGetAnalysisScope1',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getAnalysisScope1(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetAnalysisScope2 = createAsyncThunk(
  'fetchGetAnalysisScope2',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getAnalysisScope2(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchContactUs = createAsyncThunk(
  'fetchContact',
  async ({ data, isLoading, onSuccess, onError }: IFetchContactDataApi) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.contactUs(data)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
          return res?.data
        }
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetListEmission = createAsyncThunk(
  'fetchGetListEmission',
  async ({ params, isLoading, onSuccess, onError }: IGetEmissionDataAPI) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getListEmission(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetDetailEmission = createAsyncThunk(
  'fetchGetDetailEmission',
  async ({
    id,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getDetailEmission(id)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchRegistration = createAsyncThunk(
  'fetchRegistration',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IFetchURegistrationDataApi) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getRegistration(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const exportCSVRegistration = createAsyncThunk(
  'exportCSVRegistration',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IFetchURegistrationDataApi) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.exportCSVRegistration(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const downloadTemplateExcel = createAsyncThunk(
  'downloadTemplateExcel',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IDownloadExcelTemplateApi) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.downloadTemplateExcel(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const downloadTemplateScopeExcel = createAsyncThunk(
  'downloadTemplateScopeExcel',
  async ({ params, isLoading, onSuccess, onError }: any) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.downloadTemplateScopeExcel(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const downloadEmisionTemplate = createAsyncThunk(
  'downloadEmisionTemplate',
  async ({ params, isLoading, onSuccess, onError }: any) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.downloadEmissionTemplate(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchScope = createAsyncThunk(
  'fetchScope',
  async ({ params, isLoading, onSuccess, onError }: IFectchScope) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getScope(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const deleteRegistration = createAsyncThunk(
  'deleteRegistration',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.deleteRegistration(params || [])
      if (onSuccess) {
        onSuccess(res)
        return res
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchIDRegistration = createAsyncThunk(
  'fetchIDRegistration',
  async ({
    id,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getRegistrationID(id)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchUpdateIDRegistration = createAsyncThunk(
  'fetchUpdateIDRegistration',
  async ({
    id,
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.putRegistrationID(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetCountry = createAsyncThunk(
  'fetchGetCountry',
  async ({
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<IGetCountryAPIRes[]>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getCountry()
      if (onSuccess) {
        onSuccess(res?.data?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetAnalysisOther = createAsyncThunk(
  'fetchGetAnalysisOther',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getAnalysisOther(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetReport = createAsyncThunk(
  'fetchGetReport',
  async ({ params, isLoading, onSuccess, onError }: IGetReportDataAPI) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getReport(params)
      if (onSuccess) {
        onSuccess(res?.data?.data)
        return res?.data?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchManualInput = createAsyncThunk(
  'fetchContact',
  async ({ data, isLoading, onSuccess, onError }: IFetchManualDataApi) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.manuaInput(data)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
          return res?.data
        }
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchBranchSettingDetail = createAsyncThunk(
  'fetchBranchSettingDetail',
  async ({
    id,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<IGetBranchDetailRes>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getBranchSettingDetail(id)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchAddBranchSetting = createAsyncThunk(
  'fetchAddBranchSetting',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IAddBranchSettingDataAPI) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.addBranchSetting(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchPinBranchSetting = createAsyncThunk(
  'fetchPinBranchSetting',
  async ({
    params,
    id,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<IGetBranchDetailRes>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.pinBranchSetting(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchCopyBranchSetting = createAsyncThunk(
  'fetchCopyBranchSetting',
  async ({
    id,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<IGetBranchDetailRes>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.copyBranchSetting(id)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchCreateEmissionScope = createAsyncThunk(
  'fetchCreateEmissionScope',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.createEmissionScope(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchUpdateBranchSetting = createAsyncThunk(
  'fetchUpdateBranchSetting',
  async ({
    id,
    params,
    isLoading,
    onSuccess,
    onError,
  }: IUpdateBranchSettingDataAPI) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.updateBranchSetting(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchDeleteBranchSetting = createAsyncThunk(
  'fetchDeleteBranchSetting',
  async ({
    id,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.deleteBranchSetting(id)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchChangePassword = createAsyncThunk(
  'fetchChangePassword',
  async ({
    data,
    isLoading,
    onSuccess,
    onError,
  }: IFetchChangePasswordDataAPI) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.changePassword(data)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchManualUpload = createAsyncThunk(
  'fetchManualUpload',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.manualUpload(params || null)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchUploadExcel = createAsyncThunk(
  'fetchUploadExcel',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.uploadExcel(params || null)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

// 請求書AI読み取り(1ファイルずつ呼ぶ)
export const fetchAnalyzeInvoice = createAsyncThunk(
  'fetchAnalyzeInvoice',
  async ({ params, onSuccess, onError }: IParamsAPI<ISuccessAPIRes<any>>) => {
    try {
      const res = await apiHelper.analyzeInvoice(params)
      if (onSuccess) {
        onSuccess(res?.data)
      }
      return res?.data
    } catch (err) {
      const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
      if (onError) {
        onError(res?.data)
      }
      return res?.data
    }
  },
)

export const fetchGetPaymentHistory = createAsyncThunk(
  'fetchGetPaymentHistory',
  async ({ body, isLoading, onSuccess, onError }: IGetPaymentHistory) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getPaymentHistory(body)
      if (onSuccess) {
        onSuccess(res?.data?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchCreatePaymentSubscription = createAsyncThunk(
  'fetchCreatePaymentSubscription',
  async ({
    data,
    isLoading,
    onSuccess,
    onError,
  }: IPaymentCreateSubscriptionAPI) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.createSubscription(data)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchCancelPaymentSubscription = createAsyncThunk(
  'fetchCancelPaymentSubscription',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.cancelSubscription(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchCheckPaymentSubscription = createAsyncThunk(
  'fetchCheckPaymentSubscription',
  async ({
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.checkSubscription()
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetListNoti = createAsyncThunk(
  'fetchGetListNoti',
  async ({
    isLoading,
    params,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getListNoti(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetListItemScope2 = createAsyncThunk(
  'fetchGetListItemScope2',
  async ({
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getListItemScope2()
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchCreateItem = createAsyncThunk(
  'fetchCreateItem',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.createItem(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetListItemScope2ByBranch = createAsyncThunk(
  'fetchGetListItemScope2ByBranch',
  async ({
    id,
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getListItemScope2ByBranch(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetCheckSettingBranch = createAsyncThunk(
  'fetchGetCheckSettingBranch',
  async ({
    body,
    isLoading,
    onSuccess,
    onError,
  }: IGetCheckSettingBranchDataAPI) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getCheckSettingBranch(
        { ...body, branchId: undefined },
        body.branchId,
      )
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetAnalysisScope3 = createAsyncThunk(
  'fetchGetAnalysisScope3',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getAnalysisScope3(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchListImageManagement = createAsyncThunk(
  'fetchListImageManagement',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IFetchURegistrationDataApi) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getListImageManagement(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchCreateDataWithImage = createAsyncThunk(
  'fetchCreateDataWithImage',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.createDataWithImage(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetListDataEmissionScope3 = createAsyncThunk(
  'fetchGetListDataEmissionScope3',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getListDataEmissionScope3(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetSettingOption = createAsyncThunk(
  'fetchGetSettingOption',
  async ({
    option = '',
    params,
    isLoading,
    onSuccess,
    onError,
    ...props
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      props.onStart?.()
      const res = await apiHelper.getSettingOption(option, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      props.onFinally?.()
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchUpdateEmissionScope3 = createAsyncThunk(
  'fetchUpdateEmissionScope3',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.updateEmissionScope3(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchDeleteEmission = createAsyncThunk(
  'fetchDeleteEmission',
  async ({
    id,
    isLoading,
    onSuccess,
    onError,
    ...props
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      props.onStart?.()
      const res = await apiHelper.deleteEmission(id)
      if (onSuccess) {
        onSuccess(res?.data?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      props.onFinally?.()
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchCheckDeleteEmission = createAsyncThunk(
  'fetchCheckDeleteEmission',
  async ({ id, isLoading, onSuccess, onError }: IParamsAPI<any>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getCheckDeleteEmission(id)
      if (onSuccess) {
        onSuccess(res?.data?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchUpdateDataUploadImage = createAsyncThunk(
  'fetchUpdateDataUploadImage',
  async ({
    id,
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.updateRegistrationDataUploadImage(params, id)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetListGhg = createAsyncThunk(
  'fetchGetListGhg',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getListGhg(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchListDocuments = createAsyncThunk(
  'fetchListDocuments',
  async ({
    id,
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getFolder(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const moveListDocuments = createAsyncThunk(
  'moveListDocuments',
  async ({
    id,
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.moveDocument(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchCreateFolder = createAsyncThunk(
  'fetchCreateFolder',
  async ({ params, isLoading, onSuccess, onError }: IFolderData) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.createFolder(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const checkUploadFolderDuplicateName = createAsyncThunk(
  'checkUploadFolderDuplicateName',
  async ({
    id,
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.checkUploadDuplicateName(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const updateDataUploadImageFolder = createAsyncThunk(
  'updateDataUploadImageFolder',
  async ({
    id,
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.uploadFilesFolder(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetGarbages = createAsyncThunk(
  'fetchGetGarbages',
  async ({ params, onSuccess, onError }: IParamsAPI<ISuccessAPIRes<any>>) => {
    try {
      const res = await apiHelper.getGarbages(params)
      if (onSuccess) {
        onSuccess(res?.data?.data)
        return res?.data?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    }
  },
)

export const fetchDeleteGarbage = createAsyncThunk(
  'fetchDeleteGarbage',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    const { id } = params
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.deleteGarbage(id)
      if (onSuccess) {
        onSuccess(res?.data?.data)
        return res?.data?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchRestoreGarbage = createAsyncThunk(
  'fetchRestoreGarbage',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    const { id } = params
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.restoreGarabge(id)
      if (onSuccess) {
        onSuccess(res?.data?.data)
        return res?.data?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchEmptyGarbage = createAsyncThunk(
  'fetchEmptyGarbage',
  async ({
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.emptyGarabges()
      if (onSuccess) {
        onSuccess(res?.data?.data)
        return res?.data?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchRenameFolder = createAsyncThunk(
  'fetchRenameFolder',
  async ({ params, isLoading, onSuccess, onError }: IFolderData) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.renameFolder(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchUnreadNotifications = createAsyncThunk(
  'fetchUnreadNotifications',
  async ({
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getUnreadNotifications()
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchReadNotification = createAsyncThunk(
  'fetchReadOneNotification',
  async ({
    isLoading,
    id,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.readNotification(id)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchNotificationDetail = createAsyncThunk(
  'fetchNotificationDetail',
  async ({
    isLoading,
    id,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getNotificationDetail(id)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchApprovalCompany = createAsyncThunk(
  'fetchApprovalCompany',
  async ({
    isLoading,
    id,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.approvalCompany(id)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchReadNotifications = createAsyncThunk(
  'fetchReadNotifications',
  async ({
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.readNotifications()
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetListEmissionScopeById = createAsyncThunk(
  'fetchGetListEmissionScopeById',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getListEmissionScopeById(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchSettingPassword = createAsyncThunk(
  'fetchSettingPassword',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.settingPassword(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetCurrentCompanyInformation = createAsyncThunk(
  'fetchGetCurrentCompanyInformation',
  async ({
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getCurrentCompanyInformation()
      if (onSuccess) {
        store.dispatch({
          type: SET_CURRENT_COMPANY,
          data: {
            ...res?.data?.data,
            companyId: res?.data?.data?.id,
          },
        })
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchUpdateCurrentCompanyInfo = createAsyncThunk(
  'fetchUpdateCurrentCompanyInfo',
  async ({ params, isLoading, onSuccess, onError }: ICompanyAPIParams) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.updateCurrentCompanyInfomation(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetListCompany = createAsyncThunk(
  'fetchGetListCompany',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
    ...props
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      props.onStart?.()
      const res = await apiHelper.getListCompany(params)
      if (onSuccess) {
        onSuccess(res?.data?.data)
        return res?.data?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
      }
    } finally {
      props.onFinally?.()
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchCompaniesNoneGroup = createAsyncThunk(
  'fetchCompaniesNoneGroup',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
    ...props
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      props.onStart?.()
      const res = await apiHelper.getCompaniesNoneGroup(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
      }
    } finally {
      props.onFinally?.()
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGroups = createAsyncThunk(
  'fetchGroups',
  async ({
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getGroups()
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchCreateGroup = createAsyncThunk(
  'fetchCreateGroups',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.createGroup(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchEditGroup = createAsyncThunk(
  'fetchEditGroup',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.editGroup(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchMoveGroup = createAsyncThunk(
  'fetchMoveGroups',
  async ({
    id,
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.moveGroup(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchRemoveGroup = createAsyncThunk(
  'fetchRemoveGroup',
  async ({
    id,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.removeGroup(id)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchCompaniesFromSelectedGroup = createAsyncThunk(
  'fetchCompaniesFromSelectedGroup',
  async ({
    id,
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getCompaniesFromSelectedGroup(id, { params })
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchAddCompanyToGroup = createAsyncThunk(
  'fetchAddCompanyToGroup',
  async ({
    id,
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.addCompanyToGroup(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchRemoveCompanyFromGroup = createAsyncThunk(
  'fetchRemoveCompanyFromGroup',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.removeCompanyFromGroup(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetListCompanyShared = createAsyncThunk(
  'fetchGetListCompanyShare',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
    ...props
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      props.onStart?.()
      const res = await apiHelper.getListCompanyShared(params)
      if (onSuccess) {
        onSuccess(res?.data?.data)
        return res?.data?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      props.onFinally?.()
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchCompanyByCode = createAsyncThunk(
  'fetchCompanyByCode',
  async ({
    id,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    try {
      const res = await apiHelper.getCompanyByCode(id)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchInviteCompany = createAsyncThunk(
  'fetchInviteCompany',
  async ({
    id,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.inviteCompany(id)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchSharedCompany = createAsyncThunk(
  'fetchSharedCompany',
  async ({
    id,
    params,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    // if (isLoading) {
    //   store.dispatch(setLoader(true))
    // }

    try {
      const res = await apiHelper.sharedCompany(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      // if (isLoading) {
      //   store.dispatch(setLoader(false))
      // }
    }
  },
)

export const fetchCompanyDetail = createAsyncThunk(
  'fetchCompanyDetail',
  async ({
    id,
    isLoading,
    onSuccess,
    onError,
    ...props
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      props.onStart?.()
      const res = await apiHelper.getCompanyDetail(id)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      props.onFinally?.()
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetListGroupOfCurrentCompany = createAsyncThunk(
  'fetchGetListGroupOfCurrentCompany',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
    ...props
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      props.onStart?.()
      const res = await apiHelper.getListGroupOfCompany(params)
      if (onSuccess) {
        onSuccess(res?.data?.data)
        return res?.data?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
      }
    } finally {
      props.onFinally?.()
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetClassificationByScopeId = createAsyncThunk(
  'fetchGetClassificationByScopeId',
  async ({
    id,
    params,
    isLoading,
    onSuccess,
    onError,
    ...props
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      props.onStart?.()
      const res = await apiHelper.getClassificationByScopeId(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      props.onFinally?.()
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchValidateRow = createAsyncThunk(
  'fetchValidateRow',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
    ...props
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      props.onStart?.()
      const res = await apiHelper.validateRow(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      props.onFinally?.()
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetItemsByClassificationId = createAsyncThunk(
  'fetchGetItemsByClassificationId',
  async ({
    id,
    params,
    isLoading,
    onSuccess,
    onError,
    ...props
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      props.onStart?.()
      const res = await apiHelper.getItemsByClassificationId(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      props.onFinally?.()
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetEmissionsByItemId = createAsyncThunk(
  'fetchGetEmissionsByItemId',
  async ({
    id,
    params,
    isLoading,
    onSuccess,
    onError,
    ...props
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      props.onStart?.()
      const res = await apiHelper.getEmissionByItemId(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      props.onFinally?.()
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetItemsByBranch = createAsyncThunk(
  'fetchGetItemsByBranch',
  async ({
    id,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getItemsByBranchId(id)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchSearchConditions = createAsyncThunk(
  'fetchSearchConditions',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getSearchConditions(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchSaveSearchCondition = createAsyncThunk(
  'fetchSaveSearchCondition',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.saveSearchCondition(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchDeleteSelectedConditions = createAsyncThunk(
  'fetchDeleteSelectedConditions',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.deleteSelectedConditions(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchUpdateSearchCondition = createAsyncThunk(
  'fetchUpdateSearchCondition',
  async ({
    id,
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.updateSearchCondition(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchUpdateSearchConditionName = createAsyncThunk(
  'fetchUpdateSearchConditionName',
  async ({
    id,
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.updateSearchConditionName(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchAnalysisFilterSettings = createAsyncThunk(
  'fetchAnalysisFilterSettings',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getAnalysisFilterSettings(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchAnalysisFilterSetting = createAsyncThunk(
  'fetchAnalysisFilterSetting',
  async ({
    id,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getAnalysisFilterSetting(id)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchSaveAnalysisFilterSetting = createAsyncThunk(
  'fetchSaveAnalysisFilterSetting',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.saveAnalysisFilterSetting(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchUpdateAnalysisFilterSetting = createAsyncThunk(
  'fetchUpdateAnalysisFilterSetting',
  async ({
    id,
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.updateAnalysisFilterSetting(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchListShareDatas = createAsyncThunk(
  'fetchListShareDatas',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getListShareDatas(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchRequestShareDatas = createAsyncThunk(
  'fetchRequestShareDatas',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.requestShareDatas(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchListRequestShareDatas = createAsyncThunk(
  'fetchListRequestShareDatas',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getListRequestShareDatas(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchShareTotal = createAsyncThunk(
  'fetchShareTotal',
  async ({
    id,
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.shareTotal(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchUpdateTotal = createAsyncThunk(
  'fetchUpdateTotal',
  async ({
    id,
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.updateTotal(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchCompaniesApproved = createAsyncThunk(
  'fetchCompaniesApproved',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
    ...props
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      props.onStart?.()
      const res = await apiHelper.getListCompanyApproved(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      props.onFinally?.()
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetListCompanyInvited = createAsyncThunk(
  'fetchGetListCompanyInvited',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getListCompanyInvited(params)
      if (onSuccess) {
        onSuccess(res?.data?.data)
        return res?.data?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchContactUsForPremium = createAsyncThunk(
  'fetchContactUsForPremium',
  async ({ data, isLoading, onSuccess, onError }: IFetchContactDataApi) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.contactUsForPremium(data)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
          return res?.data
        }
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetBranchesFromCompany = createAsyncThunk(
  'fetchGetBranchesFromCompany',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getBranchesFromCompany(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
          return res?.data
        }
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchUpdateGroupCompanyBranches = createAsyncThunk(
  'fetchUpdateGroupCompanyBranches',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.updateGroupCompanyBranches(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
          return res?.data
        }
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchEditEmissionScope = createAsyncThunk(
  'fetchEditEmissionScope',
  async ({
    params,
    isLoading,
    id,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.editEmissionScope(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetGoalVersionList = createAsyncThunk(
  'fetchGetGoalVersionList',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getGoalVersionList(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchListCutOffPlans = createAsyncThunk(
  'fetchListCutOffPlans',
  async ({
    id,
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getListCutOffPlans(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
          return res?.data
        }
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchSettingStatusCutoffPlan = createAsyncThunk(
  'fetchSettingStatusCutoffPlan',
  async ({
    id,
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.settingStatusCutoffPlan(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
          return res?.data
        }
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchDeleteCutoffPlan = createAsyncThunk(
  'fetchDeleteCutoffPlan',
  async ({
    id,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.deleteCutoffPlan(id)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
          return res?.data
        }
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchCustomCutoff = createAsyncThunk(
  'fetchCustomCutoff',
  async ({
    id,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getCustomCutoff(id)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
          return res?.data
        }
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchUpdateCustomCutoff = createAsyncThunk(
  'fetchUpdateCustomCutoff',
  async ({
    id,
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.updateCustomCutoff(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
          return res?.data
        }
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetTotalEmissionAmountOfBaseYear = createAsyncThunk(
  'fetchGetTotalEmissionAmountOfBaseYear',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getTotalEmissionAmountOfBaseYear(
        params.year,
        params.branchId,
      )
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)
export const fetchCreateGoalVersion = createAsyncThunk(
  'fetchCreateGoalVersion',
  async ({
    isLoading,
    params,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>, IParamCreateGoalVersion>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.createGoalVersion(params!)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchVersionSelect = createAsyncThunk(
  'fetchVersionSelect',
  async ({
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getVersionSelect()
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
          return res?.data
        }
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchEmissionCutStrategies = createAsyncThunk(
  'fetchEmissionCutStrategies',
  async ({
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.getEmissionCutStrategies()
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
          return res?.data
        }
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchDuplicateGoalVersion = createAsyncThunk(
  'fetchDuplicateGoalVersion',
  async ({
    isLoading,
    id,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.duplicateGoalVersion(id)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchDeleteGoalVersion = createAsyncThunk(
  'fetchDeleteGoalVersion',
  async ({
    isLoading,
    id,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.deleteGoalVersion(id)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchDetailGoalVersion = createAsyncThunk(
  'fetchDetailGoalVersion',
  async ({
    isLoading,
    id,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getDetailGoalVersion(id)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchEditGoalVersion = createAsyncThunk(
  'fetchEditGoalVersion',
  async ({
    isLoading,
    id,
    params,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.updateGoalVersion(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchAddCutoffPlan = createAsyncThunk(
  'fetchAddCutoffPlan',
  async ({
    id,
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.addCutoffPlan(id, params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchReductionSelect = createAsyncThunk(
  'fetchReductionSelect',
  async ({
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getReductionSelect()
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchDetailCutOff = createAsyncThunk(
  'fetchDetailCutOff',
  async ({
    id,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getDetailCutOff(id)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchReductionSimulation = createAsyncThunk(
  'fetchReductionSimulation',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getReductionSimulation(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchUpdateAccountInfo = createAsyncThunk(
  'fetchUpdateAccountInfo',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.updateAccountInfo(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchUpdateAccountInfoEmail = createAsyncThunk(
  'fetchUpdateAccountInfoEmail',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.updateAccountInfoEmail(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchVerifyChangeEmail = createAsyncThunk(
  'fetchVerifyChangeEmail',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.verifyChangeEmail(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchGetClassificationElectricId = createAsyncThunk(
  'fetchGetClassificationElectricId',
  async ({
    isLoading,
    onSuccess,
    onError,
  }: IParamsAPI<ISuccessAPIRes<any>>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }
    try {
      const res = await apiHelper.getClassificationElectricId()
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const createListRegistrations = createAsyncThunk(
  'createListRegistrationData',
  async ({ params, isLoading, onSuccess, onError }: any) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.createRegistrations(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchCheckRemoveBranch = createAsyncThunk(
  'fetchCheckRemoveBranch',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
    ...props
  }: IParamsAPI<any>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      props.onStart?.()
      const res = await apiHelper.checkRemoveBranch(params)
      if (onSuccess) {
        onSuccess(res?.data?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      props.onFinally?.()
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const removeBranch = createAsyncThunk(
  'removeBranch',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
    ...props
  }: IParamsAPI<any>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      props.onStart?.()
      const res = await apiHelper.removeBranch(params)
      if (onSuccess) {
        onSuccess(res?.data?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      props.onFinally?.()
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const fetchUploadHistory = createAsyncThunk(
  'fetchUploadHistory',
  async ({
    params,
    isLoading,
    onSuccess,
    onError,
    ...props
  }: IParamsAPI<any>) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      props.onStart?.()
      const res = await apiHelper.getUploadHistory(params)
      if (onSuccess) {
        onSuccess(res?.data?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      props.onFinally?.()
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)

export const downloadHistoryCsv = createAsyncThunk(
  'downloadHistoryCsv',
  async ({ params, isLoading, onSuccess, onError }: any) => {
    if (isLoading) {
      store.dispatch(setLoader(true))
    }

    try {
      const res = await apiHelper.downloadHistoryCsv(params)
      if (onSuccess) {
        onSuccess(res?.data)
        return res?.data
      }
    } catch (err) {
      if (!(err instanceof Error)) {
        const res = err as AxiosResponse<IErrorAPIRes, AxiosError>
        if (onError) {
          onError(res?.data)
        }
        return res?.data
      }
    } finally {
      if (isLoading) {
        store.dispatch(setLoader(false))
      }
    }
  },
)
